import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import * as _ from 'lodash';

import { AppConfig } from '../../../../app.settings';
import { IAdbSpacedeskConfiguration } from '../../../../core/interfaces/adb/adb-spacedesk-configuration.interface';
import { IAdbSpacedeskStatus } from '../../../../core/interfaces/adb/adb-spacedesk-status.interface';
import { ADBConnectedDevicesModified } from '../../../../core/models/view-models/adb-models/adb-connected-devices-modified.view-model';
import { UIService } from '../../../../core/services/utils/ui.service';
import { SpaceWallPresetsService } from './preset.service';
import { ScreensStateService } from '../../../../core/services/screen-state/screens-state.service';
import { MonitorHub } from '../../../../shared-module/hubs/monitor.hub';
import { WallMonitor } from '../../../shared/models/monitor.type';
import { IUpdateConfigurationDevice } from '../../../../core/interfaces/adb/update-configuration-device.interface';
import { IupdateSpacedeskConfigurationForAllDevices } from './../../../../core/interfaces/adb/update-spacedesk-configuration-for-all-devices.interface';
import { IAdbListUpdate } from '../../../../core/interfaces/adb/adb-devices-list-update.interface';
import { AppConstants } from '../../../../app.constants';
import { IRemoveDevice } from '../../../../core/interfaces/adb/remove-device.interface';
import { Machine } from '../../../../core/models/entities/machine.type';
import { Wall } from '../../../../wall-module/shared/models/wall.type';
import { AdbAndroidIdentifier } from '../../../../core/models/view-models/adb-models/adb-android-identifier';
import { DefaultSpacedeskConfigurationParams, MultipleSpacedeskConfigurationParams } from '../../../../core/interfaces/adb/update-spacedesk-configuration.intefaces';

@Injectable()
export class AdbService {

  public listOfDevicesUpdated: Subject<IAdbListUpdate> = new Subject();
  public connectedDevicesData: ADBConnectedDevicesModified[] = [];
  public selectedDevices: BehaviorSubject<ADBConnectedDevicesModified[]> = new BehaviorSubject([]);
  public connectedDevicesList: BehaviorSubject<ADBConnectedDevicesModified[]> = new BehaviorSubject([]);
  public connectedIpAddresses: BehaviorSubject<string[]> = new BehaviorSubject([]);
  private callsCounter: number = 0;
  private endpoint: string;
  private presetsFromOtherLocations: Wall[] = [];

  constructor(
    private readonly httpClient: HttpClient,
    private readonly uiService: UIService,
    private readonly config: AppConfig,
    private readonly spaceWallPresetsService: SpaceWallPresetsService,
    private readonly monitorHub: MonitorHub,
    private readonly screensStateService: ScreensStateService
  ) {
    this.endpoint = this.config.getConfig('api') + 'ADB';

    this.uiService.ADBDeviceSelectionChanged.subscribe(() => {
      this.unSelecAll();
    });

    this.monitorHub.AdbRestartSpacedesk.subscribe(() => this.refreshWall(), () => this.refreshWall());
  }

  connectedDevices(physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ physicalAddress });
    return this.httpClient.get<void>(`${this.endpoint}/ConnectedDevices`, { params });
  }

  getConnectedDevicesToServer(): Observable<void> {
    return this.httpClient.get<void>(`${this.endpoint}/ConnectedDevicesToServer`);
  }

  enableWirelessAccess(device: string, physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ device, physicalAddress });
    return this.httpClient.get<void>(`${this.endpoint}/EnableWirelessAccess`, { params });
  }

  connectDevice(ipAdress: string, physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ ipAdress, physicalAddress });
    return this.httpClient.get<void>(`${this.endpoint}/ConnectDevice`, { params });
  }

  connectDeviceByServer(ipAdress: string): Observable<void> {
    const params: HttpParams = this.setHttpParams({ ipAdress });
    return this.httpClient.get<void>(`${this.endpoint}/ConnectDeviceByServer`, { params });
  }

  disconnectDevice(ipAdress: string, physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ ipAdress, physicalAddress });
    return this.httpClient.get<void>(`${this.endpoint}/DisconnectDevice`, { params });
  }

  disconnectDeviceByServer(ipAdress: string): Observable<void> {
    const params: HttpParams = this.setHttpParams({ ipAdress });
    return this.httpClient.get<void>(`${this.endpoint}/DisconnectDeviceByServer`, { params });
  }

  rebootDevice(ipAdress: string, physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ ipAdress, physicalAddress });
    return this.httpClient.get<void>(`${this.endpoint}/RebootDevice`, { params });
  }

  rebootDeviceByServer(ipAddress: string): Observable<void> {
    const params: HttpParams = this.setHttpParams({ ipAddress});
    return this.httpClient.get<void>(`${this.endpoint}/RebootDeviceByServer`, { params });
  }

  stopSpacedesk(ipAdress: string, physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ ipAdress, physicalAddress });
    return this.httpClient.get<void>(`${this.endpoint}/StopSpacedesk`, { params });
  }

  startSpacedesk(ipAdress: string, physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ ipAdress, physicalAddress });
    return this.httpClient.get<void>(`${this.endpoint}/StartSpacedesk`, { params });
  }

  restartSpacedesk(ipAdress: string, physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ ipAdress, physicalAddress });
    return this.httpClient.get<void>(`${this.endpoint}/RestartSpacedesk`, { params });
  }

  restartAgentApp(physicalAddress: string): Observable<void> {
    const params: HttpParams = this.setHttpParams({ physicalAddress: physicalAddress });
    return this.httpClient.get<void>(`${this.endpoint}/RestartAgentApplication`, { params });
  }

  updateSpacedesk
    (ipAddresses: string[], isDefault: boolean = true, physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ physicalAddress, isDefault });
    return this.httpClient.post<void>(`${this.endpoint}/UpdateSpacedesk`, ipAddresses, { params });
  }

  updateAndroidAgent(addresses: AdbAndroidIdentifier[]): Observable<void> {
    return this.httpClient.post<void>(`${this.endpoint}/UpdateAndroidAgent`, addresses);
  }

  uploadSpacedeskFile(file: File): Observable<void> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', '*/*');

    return this.httpClient.post<void>(`${this.endpoint}/UploadSpacedeskFile`, formData, { headers });
  }

  updateDefaultSpacedeskConfiguration(configParams: DefaultSpacedeskConfigurationParams): Observable<void> {
    const params = this.setHttpParams({
      ...configParams,
      physicalAddress: configParams.physicalAddress ?? this.uiService.selectedPhysicalAddress
    });
    return this.httpClient.get<void>(`${this.endpoint}/UpdateDefaultSpacedeskConfiguration`, { params });
  }

  updateMultipleSpacedeskConfiguration(configParams: MultipleSpacedeskConfigurationParams): Observable<void> {
    const { data, ...restParams } = configParams;
    const params = this.setHttpParams({
      ...restParams,
      physicalAddress: restParams.physicalAddress ?? this.uiService.selectedPhysicalAddress
    });
    return this.httpClient.post<void>(`${this.endpoint}/UpdateMultipleSpacedeskConfiguration`, data, { params });
  }

  getSpacedeskConfiguration
    (ipAdress: string, physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<IAdbSpacedeskConfiguration> {
    const params: HttpParams = this.setHttpParams({ ipAdress, physicalAddress });
    return this.httpClient.get<IAdbSpacedeskConfiguration>(`${this.endpoint}/GetSpacedeskConfiguration`, { params });
  }

  getSpacedeskStatus(ipAdress: string, physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<IAdbSpacedeskStatus> {
    const params: HttpParams = this.setHttpParams({ ipAdress, physicalAddress });
    return this.httpClient.get<IAdbSpacedeskStatus>(`${this.endpoint}/GetSpacedeskStatus`, { params });
  }

  getLocalIpAddress(physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ physicalAddress });
    return this.httpClient.get<void>(`${this.endpoint}/GetLocalIpAddress`, { params });
  }

  removeDeviceFromList(data: IRemoveDevice): Observable<void> {
    return this.httpClient.post<void>(`${this.endpoint}/RemoveDeviceFromList`, data);
  }

  registerDeviceKey(deviceKey: string, ipadress: string) : Observable<void> {
    const params: HttpParams = this.setHttpParams({ deviceKey, ipadress });
    return this.httpClient.post<void>(`${this.endpoint}/RegisterDeviceKey`, null, { params });
  }

  removeDeviceKeysDuplicates(deviceKeys: string[], physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ physicalAddress });
    return this.httpClient.post<void>(`${this.endpoint}/RemoveDeviceKeysDuplicates`, deviceKeys, { params });
  }

  getAllConnectedDevices(physicalAddresses: string[]): Observable<void> {
    return this.httpClient.post<void>(`${this.endpoint}/GetAllConnectedDevices`, physicalAddresses);
  }

  checkDeviceAvailable(ipAdress: string, physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const params: HttpParams = this.setHttpParams({ ipAdress, physicalAddress });
    return this.httpClient.post<void>(`${this.endpoint}/CheckDeviceAvailable`, null, { params });
  }

  updateSpacedeskConfigurationForAllDevices
    (serverIp: string, devices: IUpdateConfigurationDevice[],
      physicalAddress: string = this.uiService.selectedPhysicalAddress): Observable<void> {
    const modalData: IupdateSpacedeskConfigurationForAllDevices = {
      ServerIp: serverIp,
      PhysicalAddress: physicalAddress,
      Devices: devices
    };
    return this.httpClient.post<void>(`${this.endpoint}/UpdateSpacedeskConfigurationForAllDevices`, modalData);
  }

  public checkToAutoConnect(): void {
    const extendedDesktopsValue = _.cloneDeep(this.spaceWallPresetsService.extendedGroupSubject.value);
    if (extendedDesktopsValue) {
      extendedDesktopsValue.content = [extendedDesktopsValue.content];
      const wallMonitors: WallMonitor[] =
        _.flatten([
          ...(this.mapGroupValueToMonitorList(extendedDesktopsValue)),
          ...(this.mapGroupValueToMonitorList(this.spaceWallPresetsService.mirrorGroupsSubject.value)),
          ...(this.mapGroupValueToMonitorList(this.spaceWallPresetsService.wallGroupsSubject.value))
        ]);
      const filteredWallMonitors = wallMonitors.filter((wallMonitor: WallMonitor) => {
        const allDeviceIpAdresses: ADBConnectedDevicesModified[] = this.connectedDevicesData
          .filter((device: ADBConnectedDevicesModified) => device.IpAddress && device.IpAddress.includes('.'));
        const foundedDevice = allDeviceIpAdresses
          .find((device: ADBConnectedDevicesModified) => wallMonitor && wallMonitor.ClientIP?.includes(device.IpAddress.split(':')[0]));
        return !!!foundedDevice;
      });

      const allDevicesWitkKey: ADBConnectedDevicesModified[] = this.connectedDevicesData.filter((device: ADBConnectedDevicesModified) => {
        return device.IpAddress && !device.IpAddress.includes('.');
      });
      const allDeviceKeyToEnable: string[] = allDevicesWitkKey.map((device: ADBConnectedDevicesModified) => device.IpAddress);

      const allMonitorIpsInWall: string[] = filteredWallMonitors && filteredWallMonitors.length
        ? filteredWallMonitors.map((wallMonitor: WallMonitor) => wallMonitor.ClientIP?.split(':')[0])
        : [];

      allDeviceKeyToEnable.forEach((deviceKey: string) => {
        this.enableWirelessAccess(deviceKey).subscribe();
      });

      allMonitorIpsInWall.forEach((deviceIp: string) => {
        this.connectDevice(deviceIp).subscribe(() => {
          this.listOfDevicesUpdated.next({ ipAddressToUpdate: deviceIp, isToShowMessage: false });
        });
      });
    }
  }

  public mapGroupValueToMonitorList(wallGroup: any): WallMonitor[] {
    if (wallGroup && wallGroup.content) {
      const wallMonitors: WallMonitor[] = [];
      wallGroup.content.forEach((content: any) => {
        if (Array.isArray(content.Monitors)) {
          wallMonitors.push(content.Monitors);
        }
      });
      return wallMonitors;
    }
    return [];
  }

  public unSelecAll(): void {
    this.selectedDevices.next([]);
  }

  public selectByIds(ipAddresses: string[]): void {
    const selectedDEvices: ADBConnectedDevicesModified[] = _.filter(this.connectedDevicesList.value,
      (device: ADBConnectedDevicesModified) => _.some(ipAddresses, (ipAddress: string) =>
        ipAddress === device.IpAddress)
    );

    this.selectedDevices.next(selectedDEvices);
  }

  public deactivateDevice(ipAdress: string): void {
    const updatedList: ADBConnectedDevicesModified[] = this.connectedDevicesList.value.map((device: ADBConnectedDevicesModified) => {
      if (device.IpAddress === ipAdress) {
        device.Status = AppConstants.ADB_OFFLINE_STATUS;
      }
      return device;
    });
    this.connectedDevicesList.next(updatedList);
    this.listOfDevicesUpdated.next();
  }

  public deleteDeviceFromList(ipAdress: string): void {
    _.remove(this.connectedDevicesList.value, (device: ADBConnectedDevicesModified) => device.IpAddress === ipAdress);
    this.connectedDevicesList.next(this.connectedDevicesList.value);
  }

  public getSpacewallInitialization(): any {
    return this.spaceWallPresetsService.wallGroupsSubject.value;
  }

  public getSpacewallInitializationSub(): BehaviorSubject<any> {
    return this.spaceWallPresetsService.wallGroupsSubject;
  }

  public foundAssignedAndroidIpValue(deviceIp: string, isDeviceKeyRegistered: boolean = true, propertyName: string = 'MonitorHandler'): string {
    const extendedDesktopsValue = _.cloneDeep(this.spaceWallPresetsService.extendedGroupSubject.value);
    let physicalAddress: string = null;
    if (extendedDesktopsValue && extendedDesktopsValue.content) {
      extendedDesktopsValue.content = [extendedDesktopsValue.content];
      if (isDeviceKeyRegistered) {
        this.presetsFromOtherLocations.forEach(preset => {
          extendedDesktopsValue.content[0].Monitors.push(...preset.Monitors);
        });

        extendedDesktopsValue.content[0].Monitors = extendedDesktopsValue.content[0].Monitors.filter((monitor: WallMonitor, index: number, presets: WallMonitor[]) =>
          index === presets.findIndex((mon: WallMonitor) => (
            mon.ClientIP?.split(':')[0] === monitor.ClientIP?.split(':')[0]
          ))
        );
      }
      physicalAddress = this.findIpInWallGroup(extendedDesktopsValue, deviceIp, propertyName);
      if (!physicalAddress) {
        const wallsGroups = this.spaceWallPresetsService.wallGroupsSubject.value;
        physicalAddress = this.findIpInWallGroup(wallsGroups, deviceIp, propertyName);
      } if (!physicalAddress) {
        const mirrorGroups = this.spaceWallPresetsService.mirrorGroupsSubject.value;
        physicalAddress = this.findIpInWallGroup(mirrorGroups, deviceIp, propertyName);
      }
    }

    return physicalAddress;
  }

  public findPresetsFromAllLocations() {
    this.screensStateService.locationsChanged.pipe(
      filter((data) => !!(data && data.length)),
      take(1)
    ).subscribe((locations: Machine[]) => {
      locations.forEach((loc: Machine) => {
        if (loc.PhysicalAddress !== this.spaceWallPresetsService.selectedMachinePhysicalAddress.value) {
          this.spaceWallPresetsService.getByPhysicalAddress(loc.PhysicalAddress).subscribe((preset) => {
            this.presetsFromOtherLocations.push(...preset.filter(pres => pres.Walls[0].Monitors[0] != null).map((pres => pres.Walls[0])));
            this.presetsFromOtherLocations.push(...preset.filter(pres => pres.MirrorGroups[0].Monitors[0] != null).map((pres => pres.MirrorGroups[0])));
            this.presetsFromOtherLocations.push(...preset.filter(pres => pres.ExtendedGroup.Monitors[0] != null).map((pres => pres.ExtendedGroup)));
          })
        }
      })
    });
  }

  private findIpInWallGroup(wallGroup: any, deviceIp: string, propertyName: string): string {
    let physicalAddress = null;
    if (wallGroup && wallGroup.content && wallGroup.content.length) {
      if (wallGroup.content.length === 1) {
        physicalAddress = this.findIpInWall(wallGroup.content[0], deviceIp, propertyName);
      } else if (wallGroup.content.length > 1) {
        for (const wall of wallGroup.content) {
          physicalAddress = this.findIpInWall(wall, deviceIp, propertyName);
          if (physicalAddress) {
            break;
          }
        }
      }
    }

    return physicalAddress;
  }

  private findIpInWall(wallContent: any, deviceIp: string, propertyName: string): string {
    let physicalAddress = null;
    if (wallContent.Monitors) {
      const monitorsList: WallMonitor[] = wallContent.Monitors;
      if (monitorsList.length) {
        const selectedAdbDeviceIp: string = deviceIp.split(':')[0];
        monitorsList.forEach((monitor: WallMonitor) => {
          if (monitor.ClientIP && monitor.ClientIP.includes(selectedAdbDeviceIp)) {
            physicalAddress = monitor[propertyName].replace(/[{}]/g, '');
          }
        });
      }
    }

    return physicalAddress;
  }

  private setHttpParams(paramsToAdd: any): HttpParams {
    let params: HttpParams = new HttpParams();
    for (const key in paramsToAdd) {
      if (paramsToAdd.hasOwnProperty(key)) {
        if (key === 'ipAdress') {
          const ipAddress = paramsToAdd[key]?.split(':')[0];
          params = params.append(key, ipAddress);
        } else {
          params = params.append(key, paramsToAdd[key]);
        }
      }
    }

    return params;
  }

  private refreshWall(): void {
    if (this.uiService.selectedPhysicalAddress && this.callsCounter < 1) {
      this.callsCounter++;
      this.monitorHub.isWallRefreshing.next(true);
      setTimeout(() => {
        this.spaceWallPresetsService.refreshPresetCall(this.uiService.selectedPhysicalAddress).subscribe(
          () => this.callsCounter = 0,
          () => this.monitorHub.isWallRefreshing.next(false)
        );
      }, 6000);
    }
  }
}
